import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { login as apiLogin, getAuthConfig } from '../utils/api'
import { authslice } from '../store/auth/authSlice'
import { C, gradient, fontStack, btn, input } from '../constant/theme'

export default function Login() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [mobile, setMobile] = useState(window.innerWidth < 640)
  const [allowRegistration, setAllowRegistration] = useState(false)
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useEffect(() => {
    getAuthConfig().then((cfg) => setAllowRegistration(cfg.allowRegistration)).catch(() => {})
  }, [])

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const data = await apiLogin({ email: email.trim(), password })
      dispatch(authslice.actions.setUser({ token: data.token, user: data.user }))
      // Admins land on the dashboard; viewers go straight to the feed.
      navigate(data.user?.isAdmin ? '/admin' : '/', { replace: true })
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ ...S.root, flexDirection: mobile ? 'column' : 'row' }}>
      {!mobile && (
        <div style={S.left}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={S.brandIcon}>🧭</div>
            <h1 style={S.brandName}>Tour Timeline</h1>
            <p style={S.brandTagline}>Capture your group trips day by day — photos, videos, places & the people who were there.</p>
            <div style={S.featureList}>
              {['Day-by-day event timeline', 'Photos & videos in the cloud', 'Private — invite-only access'].map((f) => (
                <div key={f} style={S.featureItem}>
                  <span style={S.featureDot} />
                  <span style={S.featureText}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <p style={S.leftFooter}>© {new Date().getFullYear()} Tour Timeline</p>
        </div>
      )}

      <div style={mobile ? S.rightMobile : S.right}>
        <form style={S.form} onSubmit={submit}>
          <div>
            <h2 style={S.formTitle}>Welcome back</h2>
            <p style={S.formSub}>Sign in to view trips</p>
          </div>

          <Field label="Email">
            <input style={input} type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>

          <Field label="Password">
            <div style={{ position: 'relative' }}>
              <input style={input} type={showPass ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" style={S.eyeBtn} onClick={() => setShowPass(!showPass)}>{showPass ? '🙈' : '👁'}</button>
            </div>
          </Field>

          {error && <div style={S.error}>⚠️ {error}</div>}

          <button style={{ ...btn('primary'), padding: '13px', marginTop: 4 }} type="submit" disabled={busy}>
            {busy ? 'Please wait…' : 'Sign in'}
          </button>

          {allowRegistration && (
            <p style={S.registerPrompt}>
              Don't have an account?{' '}
              <Link to="/register" style={S.registerLink}>Create one</Link>
            </p>
          )}
        </form>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2e3d52' }}>{label}</label>
      {children}
    </div>
  )
}

const S = {
  root: { minHeight: '100vh', display: 'flex', fontFamily: fontStack },
  left: { width: '42%', background: gradient, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '48px 40px', overflow: 'hidden' },
  brandIcon: { width: 60, height: 60, borderRadius: 16, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, fontSize: 30 },
  brandName: { fontSize: 28, fontWeight: 800, color: '#e2eaf3', margin: '0 0 10px', letterSpacing: '-0.5px' },
  brandTagline: { fontSize: 15, color: 'rgba(200,215,230,0.65)', lineHeight: 1.6, margin: '0 0 40px', maxWidth: 280 },
  featureList: { display: 'flex', flexDirection: 'column', gap: 14 },
  featureItem: { display: 'flex', alignItems: 'center', gap: 10 },
  featureDot: { width: 7, height: 7, borderRadius: '50%', background: '#4db6ac', flexShrink: 0 },
  featureText: { fontSize: 14, color: 'rgba(200,215,230,0.72)' },
  leftFooter: { fontSize: 12, color: 'rgba(180,200,220,0.28)', margin: 0 },
  right: { flex: 1, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' },
  rightMobile: { flex: 1, background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', minHeight: '100vh' },
  form: { width: '100%', maxWidth: 380, background: C.card, borderRadius: 20, padding: '36px 32px', boxShadow: '0 6px 28px rgba(13,27,42,.13)', border: `1px solid ${C.cardBorder}`, display: 'flex', flexDirection: 'column', gap: 18 },
  formTitle: { fontSize: 22, fontWeight: 800, color: C.text, margin: '0 0 6px', letterSpacing: '-0.3px' },
  formSub: { fontSize: 13.5, color: C.textSoft, margin: 0 },
  eyeBtn: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 },
  error: { background: C.dangerBg, color: C.danger, borderRadius: 10, padding: '10px 14px', fontSize: 13, border: '1px solid #d4a8a8' },
  registerPrompt: { margin: 0, fontSize: 13, color: C.textSoft, textAlign: 'center' },
  registerLink: { color: C.navy2, fontWeight: 700, textDecoration: 'none' },
}
